// client/customer/src/pages/Reserve/by-service/SelectTime.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import {
  fetchAvailableTimesForDate,
  type DateFlowTimeSlot,
} from '../../../utils/reservation/by-Date/fetchAvailableTimesForDate';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import {
  postAvailableTimesByService,
  type AvailableTimeSlot,
} from '../../../utils/reservation/by-service/postAvailableTimesByService';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';
import { isPastTimeSlot } from '../../../utils/reservation/filterPastTimeSlots';

// ✅ 共通UI
import ReserveStepHeader from '../ReserveStepHeader';
import ReserveStepFooter from '../ReserveStepFooter';
import { useI18n } from '../../../i18n';

const SelectTimeByService: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const isRebooking = searchParams.get('rebook') === '1';
  const {
    storeId,
    selectedDate,
    selectedStaffId,
    isFreeReservation,
    selectedServices,
    selectedStartTime,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedServices,
    setIsFreeReservation,
  } = useReserveContext();

  const serviceIds = useMemo(
    () =>
      (selectedServices || [])
        .map((s) => Number(s.serviceId))
        .filter((n) => Number.isFinite(n) && n > 0),
    [selectedServices]
  );

  const [slots, setSlots] = useState<Array<AvailableTimeSlot | DateFlowTimeSlot>>([]);
  const [loading, setLoading] = useState(false);
  const totalMinutes = useMemo(
    () => (selectedServices || []).reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0),
    [selectedServices]
  );

  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-service/store');
      return;
    }
    if (serviceIds.length === 0) {
      toast.info(t('date.selectServiceFirst'));
      navigate('/reserve/by-service/service');
      return;
    }
    if (!selectedDate) {
      toast.info(t('date.chooseTitle'));
      navigate(isRebooking ? '/reserve/by-service/date?rebook=1' : '/reserve/by-service/date');
      return;
    }
    if (isRebooking && !selectedStaffId) {
      toast.info(t('date.selectStaffFirst'));
      navigate(isRebooking ? '/reserve' : '/reserve/by-service/staff');
      return;
    }
    if (isEditing && !selectedStaffId && !isFreeReservation) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-service/staff');
      return;
    }

    const load = async () => {
      try {
        setLoading(true);

        if (!isEditing && !isRebooking) {
          const baseSlots = await fetchAvailableTimesForDate(storeId, selectedDate);
          const filteredSlots = await Promise.all(
            baseSlots.map(async (slot) => {
              if (!slot.available) return slot;
              const options = await fetchStaffOptionsForDate({
                storeId,
                date: selectedDate,
                startTime: slot.time,
                serviceIds,
              });
              return {
                ...slot,
                available: options.staffs.length > 0 || options.free_available,
              };
            }),
          );
          setSlots(filteredSlots);
          return;
        }

        if (isEditing && isFreeReservation) {
          const baseSlots = await fetchAvailableTimesForDate(storeId, selectedDate);
          const filteredSlots = await Promise.all(
            baseSlots.map(async (slot) => {
              if (!slot.available) return slot;
              const options = await fetchStaffOptionsForDate({
                storeId,
                date: selectedDate,
                startTime: slot.time,
                serviceIds,
              });
              return {
                ...slot,
                available: options.free_available,
              };
            }),
          );
          setSlots(filteredSlots);
          return;
        }

        const currentStaffId = selectedStaffId;
        if (!currentStaffId) {
          return;
        }

        // ✅ 1) staff別durationで selectedServices を補正
        const durations = await fetchStaffServiceDurations(currentStaffId, serviceIds);
        const map = new Map(durations.map((d) => [Number(d.service_id), Number(d.duration_minutes)]));

        const patched = (selectedServices || []).map((s) => {
          const did = map.get(Number(s.serviceId));
          return did != null && Number.isFinite(did) && did > 0
            ? { ...s, duration_minutes: did }
            : s; // フォールバックは既存値（services.duration_minutes）
        });

        // durationが変わるならContext更新（Confirm側にも反映）
        const changed = patched.some((p, i) => Number(p.duration_minutes) !== Number(selectedServices?.[i]?.duration_minutes));
        if (changed) setSelectedServices(patched);

        const durationMinutes = patched.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0);
        if (!durationMinutes || durationMinutes <= 0) {
          toast.error(t('time.invalidDuration'));
          navigate('/reserve/by-service/service');
          return;
        }

        // ✅ 2) 正しいdurationで枠取得
        const data = await postAvailableTimesByService({
          store_id: storeId,
          staff_id: currentStaffId,
          date: selectedDate,
          duration_minutes: durationMinutes,
          service_ids: serviceIds,
        });

        setSlots(data);
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || t('time.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    storeId,
    selectedDate,
    selectedStaffId,
    isFreeReservation,
    selectedServices,
    serviceIds,
    isEditing,
    isRebooking,
    navigate,
    setSelectedServices,
    t,
  ]);

  useEffect(() => {
    if (!selectedStartTime || !selectedDate) return;

    const selectedSlot = slots.find((slot) => slot.time === selectedStartTime);
    if (!selectedSlot || !selectedSlot.available || isPastTimeSlot(selectedDate, selectedStartTime)) {
      setSelectedStartTime(null);
    }
  }, [slots, selectedDate, selectedStartTime, setSelectedStartTime]);

  const handleSelectTime = (time: string, available: boolean) => {
    if (!available || (selectedDate && isPastTimeSlot(selectedDate, time))) return;
    setSelectedStartTime(time);
    if (!isEditing && !isRebooking) {
      setSelectedStaffId(null);
      setSelectedStaffName(null);
      setIsFreeReservation(false);
    }
  };

  const handleNext = () => {
    if (!selectedStartTime) {
      toast.warning(t('time.selectWarning'));
      return;
    }
    navigate(
      isRebooking
        ? '/reserve/by-service/confirm-reservation?rebook=1'
        : isEditing
        ? '/reserve/by-service/confirm-reservation'
        : '/reserve/by-service/staff',
    );
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('time.chooseTitle')}
        onBack={() =>
          navigate(
            isEditing
              ? '/reserve/by-service/confirm-reservation'
              : isRebooking
              ? '/reserve/by-service/date?rebook=1'
              : '/reserve/by-service/date'
          )
        }
        step={isRebooking ? 2 : 4}
        totalSteps={isRebooking ? 3 : 5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">{t('time.loading')}</p>
      ) : slots.length === 0 ? (
        isEditing || isRebooking ? (
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-rose-700">{t('time.cannotChange')}</div>
                <div className="mt-1 text-sm leading-6 text-rose-600">
                  {isRebooking
                    ? t('time.noRebookingTimes')
                    : t('time.noAlternativeServiceFlow')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-400 text-sm">{t('time.noAvailableShort')}</p>
        )
      ) : (
        <div className="rounded-[28px] border border-[#d7efee] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))] p-4 shadow-[0_14px_30px_rgba(10,186,181,0.08)]">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5a9d99]">
            {t('time.availableSlots')}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {slots.map((s) => {
              const isSelected = selectedStartTime === s.time;
              const isPast = selectedDate ? isPastTimeSlot(selectedDate, s.time) : false;
              const isDisabled = !s.available || isPast;
              return (
                <button
                  key={s.time}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleSelectTime(s.time, s.available)}
                  className={[
                    'min-h-[52px] rounded-2xl border text-sm font-semibold transition',
                    isDisabled
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                      : isSelected
                      ? 'border-[#0ABAB5] bg-[#0ABAB5] text-white shadow-[0_12px_22px_rgba(10,186,181,0.24)]'
                      : 'border-[#d5ecea] bg-white text-slate-700 hover:border-[#9fdeda] hover:bg-[#f4fcfb]',
                  ].join(' ')}
                >
                  {s.time}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ReserveStepFooter
        summary={
          selectedStartTime
            ? t('time.selectedSummaryWithMinutes', { time: selectedStartTime, minutes: totalMinutes })
            : t('time.selectSummaryWithMinutes', { minutes: totalMinutes })
        }
        primaryLabel={isRebooking ? t('time.reviewRebooking') : t('footer.next')}
        onPrimary={handleNext}
        primaryDisabled={!selectedStartTime}
      />
    </div>
  );
};

export default SelectTimeByService;
